import React, { useMemo } from 'react';
import { Property, AppUser } from '../../../types';
import { PropertyCard } from '../PropertyCard';
import { PaginationControls } from '../PaginationControls';
import { MapPinIcon, ChevronLeftIcon, XMarkIcon } from '../../Icons';
import { usePropertyCommentCounts } from '../../../hooks/usePropertyCommentCounts';

interface MapSidebarCardsProps {
  totalCount: number;
  hasSpatialFilter: boolean;
  selectedProperty: Property | null;
  onClearSelectedProperty: () => void;
  clusterProperties: Property[] | null;
  isLoadingCluster: boolean;
  clusterTotal: number;
  clusterPage: number;
  clusterTotalPages: number;
  onClusterPageChange: (page: number) => void;
  onClearCluster: () => void;
  defaultProperties: Property[];
  isLoadingDefault: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  currentSelectedPropertiesForCsv: string[];
  onToggleSelectPropertyForCsv: (id: string) => void;
  onViewProperty: (property: Property) => void;
  onShareProperty: (property: Property) => void;
  appUser: AppUser | null;
  onInterested: (property: Property) => void;
  inmobiliariasConfig: Record<string, any>;
}

export const MapSidebarCards: React.FC<MapSidebarCardsProps> = (props) => {
  const {
    totalCount, hasSpatialFilter, selectedProperty, onClearSelectedProperty,
    clusterProperties, isLoadingCluster, clusterTotal, clusterPage, clusterTotalPages,
    onClusterPageChange, onClearCluster, defaultProperties, isLoadingDefault,
    currentPage, totalPages, onPageChange, currentSelectedPropertiesForCsv,
    onToggleSelectPropertyForCsv, onViewProperty, onShareProperty,
    appUser, onInterested, inmobiliariasConfig,
  } = props;

  const displayProperties = useMemo(() => defaultProperties.slice(0, 5), [defaultProperties]);

  const sidebarCodes = useMemo(() => {
    const codes = displayProperties.map((p) => p.codigo);
    if (selectedProperty?.codigo) codes.push(selectedProperty.codigo);
    if (clusterProperties) codes.push(...clusterProperties.map((p) => p.codigo));
    return codes;
  }, [displayProperties, selectedProperty?.codigo, clusterProperties]);

  const { getCount } = usePropertyCommentCounts(sidebarCodes, appUser);

  const renderCard = (p: Property, isHighlight = false) => (
    <PropertyCard
      key={p.id}
      property={p}
      isSelected={currentSelectedPropertiesForCsv.includes(p.id)}
      onView={() => onViewProperty(p)}
      onShare={() => onShareProperty(p)}
      onToggleSelect={() => onToggleSelectPropertyForCsv(p.id)}
      isCompact={true}
      isHighlighted={isHighlight}
      source={(p as any).sourceCollection}
      appUser={appUser}
      onInterested={onInterested}
      inmobiliariasConfig={inmobiliariasConfig}
      commentCount={getCount(p.codigo)}
    />
  );

  return (
    <div className="h-full w-full md:w-1/3 lg:w-1/4 overflow-hidden bg-white border-l border-gray-100 flex flex-col">
      <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <MapPinIcon className="w-4 h-4 text-sky-600" />
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">
            {hasSpatialFilter ? 'Zona seleccionada' : 'Resultados en mapa'} ({totalCount})
          </p>
        </div>
      </div>

      <div className="flex-grow overflow-y-auto p-4 space-y-4 custom-scrollbar">
        {selectedProperty ? (
          <div className="animate-fade-in space-y-3">
            <button
              onClick={onClearSelectedProperty}
              className="mb-2 text-[10px] font-black text-sky-600 uppercase flex items-center hover:text-sky-800 tracking-widest"
            >
              <ChevronLeftIcon className="w-4 h-4 mr-1" /> Volver a resultados
            </button>
            {renderCard(selectedProperty, true)}
          </div>
        ) : clusterProperties ? (
          <div className="animate-fade-in space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <h3 className="font-black text-gray-800 text-[10px] uppercase tracking-widest">
                Grupo en esta zona ({clusterTotal || clusterProperties.length})
              </h3>
              <button onClick={onClearCluster} className="p-1 hover:bg-gray-100 rounded-full" title="Cerrar grupo">
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            {isLoadingCluster ? (
              <div className="text-center py-10 text-xs text-gray-400 font-medium">Cargando inmuebles del grupo...</div>
            ) : (
              <div className="space-y-4">
                {clusterProperties.slice(0, 5).map((p) => renderCard(p))}
                {clusterTotalPages > 1 && (
                  <div className="pt-2 border-t border-gray-100 scale-90">
                    <PaginationControls currentPage={clusterPage} totalPages={clusterTotalPages} onPageChange={onClusterPageChange} />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {displayProperties.map((p) => renderCard(p))}
            {!isLoadingDefault && totalPages > 1 && (
              <div className="pt-4 border-t border-gray-100 scale-90">
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
              </div>
            )}
            {displayProperties.length === 0 && !isLoadingDefault && (
              <div className="text-center py-20">
                <MapPinIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">No se encontraron resultados</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
